import React, { createContext, useContext, useState, useEffect } from "react";
import { connect } from "react-redux";
import ArrowLeft from '@material-ui/icons/ArrowBackIos';
import axios from "axios";
import "./dev-rate-update.scss";
import TuringLogo from "../../../assets/img/turing_logo_text.svg";
import HourGlass from "../../../assets/img/hourglass.svg";
import ProfileActions from "../../legacy_components/UI_V2_Components/profile/Profile.actions";

const token = localStorage.getItem("token");
if (token) {
  axios.defaults.headers.common.Authorization = token;
}

export const DevUpdateUrls = {
  ATTEMPT: "/update-rate/attempt",
  INELIGIBLE: "/update-rate/ineligible",
  SUCCESS: "/update-rate/success",
  HIGH_RATE: "/update-rate/high",
  REVIEW: "/update-rate/review",
  NOT_ACCEPTED: "/update-rate/not-accepted",
  CONFIRM_CALL_SCHEDULE: "/update-rate/schedule-confirmed",
  UPDATE_INELIGIBLE: "/update-rate/ineligible-update",
};

export const excludeRedirectCheckUrls = [
  DevUpdateUrls.CONFIRM_CALL_SCHEDULE,
  DevUpdateUrls.UPDATE_INELIGIBLE
];

export const redirectIfNotCurrent = (route, callback) => {
  if (window.location.pathname !== route) {
    window.location.href = route;
    callback();
  }
};

export const TuringHeader = ({ alternate = false }) => (
  <header className="dev-rate-update-header">
    <img
      className="turing-logo-text"
      src={TuringLogo}
      alt="Turing"
    />
  </header>
);

export const Header = ({ title, children, alternate = false, className, showBack, onBack }) => (
  <header className={`header ${alternate ? "alternate" : ""} ${className}`}>
    <div className={`para ${alternate ? "alternate" : ""}`}>
      {showBack && <div className="go-back-btn" onClick={onBack}><ArrowLeft /> Go back</div>}
      {alternate ? <h3>{title}</h3> : <h1>{title}</h1>}
      <div>{children}</div>
    </div>
  </header>
);

export const Footer = () => (
  <footer className="footer">© {new Date().getFullYear()} Turing Enterprises, Inc.</footer>
);

export const DefaultLoadingScreen = ({
  text = "Fetching Information...",
}) => (
  <>
    <LoaderModal text={text} visible={true} />
  </>
);

const CommonInfoContext = createContext();

const CommonProvideComponent = ({ commonInfo, dispatch, children }) => {
  const [loading, setLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [failure, setFailure] = useState(false);

  const [rateData, setRateData] = useState({
    rateUpdated: false,
    submittedRate: 0,
    commonData: commonInfo,
    eligible: true,
    remainingAttempts: 2,
    inRange: false,
  });

  useEffect(() => {
    getDevRateReductionData();
  }, []);

  const getDevRateReductionData = () => {
    setLoading(true);
    setFailure(false);
    axios.get(`${ApiConstant.BASE_URL}rates/get-reduction-page-info`).then((res) => {
      const {
        data: {
          data: {
            eligible = true,
            remaining_attempts = 2,
            rate_updated = 0,
            last_attempt = {},
            userInfo: {
              first_name: firstName,
              years_of_experience: yearsOfExperience,
              expected_rate: salary,
              expected_rate_currency: expectedRateCurrency = "USD",
              negotiated_rate: negotiatedRate = 0,
              negotiated_rate_currency: negotiatedRateCurrency = "USD"
            } = {},
            rate_submitted: rateSubmitted = false,
          } = {},
        } = {},
      } = res;

      const {
        attempted_rate: last_attempted_rate = 0,
        in_market_range: last_attempted_in_market_range = false,
        lower_than_current_rate = false,
      } = last_attempt || {};

      setRateData(prevState => ({
        ...prevState,
        eligible: eligible,
        remainingAttempts: remaining_attempts,
        rateUpdated: rate_updated,
        submittedRate: last_attempted_rate,
        rateSubmitted,
        inRange: last_attempted_in_market_range,
        rateHigherThanCurrent: !lower_than_current_rate,
        firstName,
        yearsOfExperience,
        salary,
        expectedRateCurrency,
        negotiatedRate,
        negotiatedRateCurrency
      }));
      setTimeout(() => {
        setLoading(false);
      }, 100)
    }).catch((err) => {
      console.log(err);
      setLoading(false);
      setFailure(true);
    });
  };

  useEffect(() => {
    setRateData(prevState => ({
      ...prevState,
      ...commonInfo,
    }));
  }, [commonInfo]);

  const saveExpectedSalary = (data, callback = null) => {
    if (!formSubmitting) setFormSubmitting(true);
    const {
      salary,
      comment = null
    } = data;
    setFailure(false);
    axios
      .post(`${ApiConstant.BASE_URL}rates/update-with-validation`, {
        expected_salary: salary,
        comment,
      })
      .then((res) => {
        const {
          data: {
            rate_updated = false,
          } = {},
        } = res;
        setLoading(false);
        setTimeout(() => {
          if (callback) {
            callback(rate_updated);
          }
        }, 100);
      }).catch((err) => {
        console.log(err);
        setLoading(false);
        setFormSubmitting(false);
        setTimeout(() => {
          setFailure(true);
        }, 100);
      });
  };

  const submitLastAttemptedRate = (comment, callback = null) => {
    if (!formSubmitting) setFormSubmitting(true);
    setFailure(false);
    axios
      .post(`${ApiConstant.BASE_URL}rates/submit-last-attempt`, {
        comment,
      })
      .then((res) => {
        const {
          data: {
            data: {
              success = false
            } = {},
          } = {},
        } = res;
        setFormSubmitting(false);
        setTimeout(() => {
          if (callback) {
            callback(success);
          }
        }, 100);
      }).catch((err) => {
        console.log(err);
        setFormSubmitting(false);
        setLoading(false);
        setFailure(true);
      });
  };

  const saveProfileData = ({ salary, comment, ...restData }, callback) => {
    setLoading(true);
    setFailure(false);
    dispatch(ProfileActions.UPDATE_PROFILE_DATA(restData, () => { }, true));
    saveExpectedSalary({ salary, comment }, callback);
  };

  return (
    <CommonInfoContext.Provider
      value={{
        ...rateData,
        loading,
        formSubmitting,
        failure,
        saveProfileData,
        saveExpectedSalary,
        submitLastAttemptedRate,
      }}
    >
      {children}
    </CommonInfoContext.Provider>
  );
};

export const CommonInfoProvider = connect()(CommonProvideComponent);

export const useCommonInfo = () => {
  return useContext(CommonInfoContext);
};

import { Button, Modal } from "antd";
import { ApiConstant } from "../../constants";
import { formatCurrency } from "../../legacy_components/util";
import { NetworkFailure } from "./Trial";

export const LoaderModal = ({ text, visible }) => {
  return (
    <Modal
      visible={visible}
      centered
      footer={null}
      closable={false}
      maskClosable={false}
    >
      <div style={{ textAlign: "center", padding: "20px", color: "#2E6EDF" }}>
        <img src={HourGlass} alt="Loading" style={{ paddingBottom: "15px" }} />
        <p>{text}</p>
      </div>
    </Modal>
  );
};

export const FailureModal = () => (
  <Modal
    visible={true}
    centered
    footer={null}
    closable={false}
    maskClosable={false}
  >
    <NetworkFailure onButtonClick={() => location.reload()} />
  </Modal>
);


export const GoToDeveloperButton = () => (
  <Button
    type="primary"
    onClick={() => {
      localStorage.removeItem('redirect_url');
      window.location = "/dashboard/home";
    }}
  >
    Go to Talent Portal
  </Button>
);

export const Message = ({ type = "info", children }) => (
  <div className={`message ${type}`}>
    {children}
  </div>
);

export const getFormattedCurrentSalaryText = () => {
  const { negotiatedRate = 0, negotiatedRateCurrency = "USD", salary = 0, expectedRateCurrency } = useCommonInfo();
  return negotiatedRate ? `${formatCurrency(negotiatedRate)} ${negotiatedRateCurrency} per year ` : `${formatCurrency(salary)} ${expectedRateCurrency} per year`;
}

export const CurrentSalaryInfo = () => (
  <Message type="info">
    Current expected rate:{" "} <br /> {getFormattedCurrentSalaryText()}
  </Message>
);